import React from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, CheckCircle2, DollarSign, Wallet, Receipt, Scale, PhoneMissed, MessageSquare } from "lucide-react";
import { money, moneySigned, TrendChip, Overline, Sk } from "./singleUtils";

const Kpi = ({ icon: Icon, tone, label, value, sub, trend, invertTrend, onClick, testid, loading, valueClass = "text-foreground", mobileOnly }) => (
  <button
    type="button"
    onClick={onClick}
    className={`min-w-[152px] flex-1 snap-start rounded-md border border-border bg-card p-3 text-left transition-colors hover:border-amber-500/50 ${mobileOnly ? "lg:hidden" : ""}`}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 mb-1.5">
      <span className={`w-5 h-5 rounded-sm flex items-center justify-center ${tone}`}><Icon className="w-3 h-3" /></span>
      <Overline>{label}</Overline>
    </div>
    {loading ? <Sk className="h-7 w-24" /> : (
      <p className={`text-[22px] leading-7 font-extrabold font-mono tracking-tight ${valueClass}`}>{value}</p>
    )}
    <div className="mt-0.5 min-h-[15px]">
      {trend !== undefined ? <TrendChip pct={trend} invert={invertTrend} /> : sub ? <span className="text-[10px] text-muted-foreground">{sub}</span> : null}
    </div>
  </button>
);

const KpiStrip = ({ kpis, financial, comms, financialAccess, loading }) => {
  const navigate = useNavigate();
  const k = kpis || {};
  const f = financial || {};
  const c = comms || {};
  const op = f.operating_result;
  return (
    <div className="flex gap-2.5 overflow-x-auto snap-x pb-1 lg:grid lg:grid-cols-6 lg:overflow-visible" data-testid="single-kpi-strip">
      <Kpi icon={Briefcase} tone="bg-indigo-500/10 text-indigo-600 dark:text-indigo-400" label="Active Jobs" loading={loading}
        value={k.active_jobs ?? 0} sub="all open statuses" onClick={() => navigate("/dashboard/jobs")} testid="kpi-card-active-jobs" />
      <Kpi icon={CheckCircle2} tone="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" label="Completed" loading={loading}
        value={k.completed_jobs ?? 0} trend={k.completed_trend} onClick={() => navigate("/dashboard/jobs")} testid="kpi-card-completed" />
      {financialAccess && (
        <Kpi icon={DollarSign} tone="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" label="Collected" loading={loading}
          value={money(k.money_collected)} trend={k.collected_trend} onClick={() => navigate("/dashboard/financials")} testid="kpi-card-collected" />
      )}
      {financialAccess && (
        <Kpi icon={Wallet} tone="bg-rose-500/10 text-rose-600 dark:text-rose-500" label="Outstanding" loading={loading}
          value={money(k.outstanding_balance)} sub="unpaid balances" valueClass={(k.outstanding_balance || 0) > 0 ? "text-rose-600 dark:text-rose-500" : "text-foreground"}
          onClick={() => navigate("/dashboard/invoices?status=open")} testid="kpi-card-outstanding" />
      )}
      {financialAccess && (
        <Kpi icon={Receipt} tone="bg-amber-500/15 text-amber-600 dark:text-amber-400" label="Expenses" loading={loading}
          value={money(k.expenses_total)} trend={k.expenses_trend} invertTrend onClick={() => navigate("/dashboard/expenses")} testid="kpi-card-expenses" />
      )}
      {financialAccess && (
        <Kpi icon={Scale} tone="bg-amber-500/15 text-amber-600 dark:text-amber-400" label="Operating Result" loading={loading}
          value={op === null || op === undefined ? "—" : moneySigned(op)}
          valueClass={(op || 0) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-500"}
          trend={f.operating_trend} onClick={() => navigate("/dashboard/financials")} testid="kpi-card-net-operating-result" />
      )}
      {/* Mobile-priority cards — desktop shows these in the Comms panel */}
      <Kpi icon={PhoneMissed} tone="bg-rose-500/10 text-rose-600 dark:text-rose-500" label="Missed Calls" loading={loading} mobileOnly
        value={c.missed_calls ?? 0} sub="in period" valueClass={(c.missed_calls || 0) > 0 ? "text-rose-600 dark:text-rose-500" : "text-foreground"}
        onClick={() => navigate("/dashboard/calls?filter=missed")} testid="kpi-card-missed-calls" />
      <Kpi icon={MessageSquare} tone="bg-amber-500/15 text-amber-600 dark:text-amber-400" label="Unread SMS" loading={loading} mobileOnly
        value={c.unread_conversations ?? 0} sub="open conversations" onClick={() => navigate("/dashboard/messages?filter=unread")} testid="kpi-card-unread-sms" />
    </div>
  );
};

export default KpiStrip;
